import type { RoleSource } from '../../types/framework'

export const cmo: RoleSource = {
  id: 'cmo',
  name: 'Chief Marketing Officer',
  group: 'Executive',
  domains: [
    {
      name: 'Brand Management & Reputation',
      characteristic: [
        [445, "Brand Trust Stewardship", "Demonstrates stewardship of brand trust."],
        [446, "Consistency", "Demonstrates consistency orientation."],
        [447, "Reputation Risk Awareness", "Demonstrates sensitivity to reputation risk."],
        [448, "Creative Judgement", "Demonstrates creative judgment."],
        [449, "Brand Equity Awareness", "Demonstrates awareness of brand equity."],
        [450, "Authenticity", "Demonstrates commitment to authenticity."],
      ],
      behaviour: [
        [451, "Brand Management and Reputation", "Maintains consistent brand identity and messaging across touchpoints."],
        [452, "Brand Management and Reputation", "Protects brand differentiation while adapting to relevant audiences."],
        [453, "Brand Management and Reputation", "Monitors brand health, sentiment and reputation indicators."],
        [454, "Brand Management and Reputation", "Coordinates response to brand issues and reputational threats."],
        [455, "Brand Management and Reputation", "Ensures brand claims are accurate, substantiated and responsible."],
        [456, "Brand Management and Reputation", "Evaluates the long-term effect of campaigns on brand equity."],
      ],
    },
    {
      name: 'Corporate Communications, Stakeholders & Crisis Response',
      characteristic: [
        [457, "Stakeholder Communication", "Demonstrates communication judgment."],
        [458, "Brand Management and Reputation", "Demonstrates reputation awareness."],
        [459, "Composure and Resilience", "Demonstrates composure under scrutiny."],
        [460, "Empathy for affected audiences", "Demonstrates empathy for affected audiences."],
        [461, "Commitment to accuracy", "Demonstrates commitment to accuracy."],
        [462, "Stakeholder Engagement and Accountability", "Demonstrates ability to align diverse stakeholders."],
      ],
      behaviour: [
        [463, "Stakeholder Communication", "Coordinates marketing and corporate communications around consistent messages."],
        [464, "Brand Management and Reputation", "Prepares response protocols for reputational and communication crises."],
        [465, "Ensuring public claims and statements are , accurate", "Ensures public claims and statements are timely, accurate and approved."],
        [466, "Stakeholder Communication", "Works with leadership, legal and relevant teams on sensitive communications."],
        [467, "Stakeholder Communication", "Monitors stakeholder reaction and adjusts communication where evidence warrants."],
        [468, "Stakeholder Communication", "Reviews communication outcomes and strengthens preparedness after incidents."],
      ],
    },
    {
      name: 'Customer Experience, Loyalty & Retention',
      characteristic: [
        [469, "Customer and User Focus", "Demonstrates commitment to customer relationships."],
        [470, "Empathy", "Demonstrates empathy."],
        [471, "Awareness of lifetime value", "Demonstrates awareness of lifetime value."],
        [472, "Long-term relationship orientation", "Demonstrates long-term relationship orientation."],
        [473, "Sensitivity to friction points", "Demonstrates sensitivity to friction points."],
        [474, "Customer and User Focus", "Demonstrates respect for customer trust."],
      ],
      behaviour: [
        [475, "Customer and User Focus", "Maps customer journeys and identifies retention or experience gaps."],
        [476, "Coordination of programs to improve loyalty, engagement and", "Coordinates programs to improve loyalty, engagement and repeat purchase."],
        [477, "Application of churn, feedback and behavioural data to", "Uses churn, feedback and behavioural data to identify at-risk segments."],
        [478, "Personalizes engagement within appropriate consent and privacy boundaries", "Personalizes engagement within appropriate consent and privacy boundaries."],
        [479, "Collaborative Working on with service teams to address", "Works with service teams to address recurring experience problems."],
        [480, "Customer and User Focus", "Measures retention and lifetime value outcomes from customer programs."],
      ],
    },
    {
      name: 'Customer Insight & Market Intelligence',
      characteristic: [
        [481, "Customer and User Focus", "Demonstrates empathy for customers."],
        [482, "Curiosity about behaviour", "Demonstrates curiosity about behaviour."],
        [483, "Analytical Thinking and Evidence Use", "Demonstrates analytical orientation."],
        [484, "Analytical Thinking and Evidence Use", "Demonstrates openness to contradictory evidence."],
        [485, "Awareness of cultural context", "Demonstrates awareness of cultural context."],
        [486, "Integrity and Ethical Leadership", "Demonstrates respect for research integrity."],
      ],
      behaviour: [
        [487, "Customer and Market Insight", "Integrates customer research, behavioural data and market intelligence."],
        [488, "Change Management", "Identifies unmet needs and changes in customer expectations."],
        [489, "Tests assumptions about segments, motivations and barriers", "Tests assumptions about segments, motivations and barriers."],
        [490, "Customer and Market Insight", "Shares actionable customer insights with product, sales and service teams."],
        [491, "Monitoring of competitors and market shifts systematically", "Monitors competitors and relevant market shifts systematically."],
        [492, "Checks that research methods and samples support reliable", "Checks that research methods and samples support reliable conclusions."],
      ],
    },
    {
      name: 'Demand Generation & Campaign Effectiveness',
      characteristic: [
        [493, "Results orientation", "Demonstrates results orientation."],
        [494, "Creative curiosity", "Demonstrates creative curiosity."],
        [495, "Commercial discipline", "Demonstrates commercial discipline."],
        [496, "Comfort with testing", "Demonstrates comfort with testing."],
        [497, "Understanding of conversion journeys", "Demonstrates understanding of conversion journeys."],
        [498, "Balanced short- and long-term perspective", "Demonstrates balanced short- and long-term perspective."],
      ],
      behaviour: [
        [499, "Designs campaigns with clear audiences, objectives and success", "Designs campaigns with clear audiences, objectives and success measures."],
        [500, "Tests creative, channel and offer alternatives to improve", "Tests creative, channel and offer alternatives to improve effectiveness."],
        [501, "Capacity and Resource Optimisation", "Coordinates campaigns with sales capacity and commercial priorities."],
        [502, "Analytical Thinking and Evidence Use", "Monitors funnel performance and reallocates spend based on evidence."],
        [503, "Leadership Effectiveness", "Ensures leads and opportunities are handed over effectively."],
        [504, "Review of campaign outcomes and applies lessons to", "Reviews campaign outcomes and applies lessons to future activity."],
      ],
    },
    {
      name: 'Digital Marketing & Channel Strategy',
      characteristic: [
        [505, "Digital fluency", "Demonstrates digital fluency."],
        [506, "Change Management", "Demonstrates adaptability to channel changes."],
        [507, "Customer and User Focus", "Demonstrates customer-journey awareness."],
        [508, "Analytical Thinking and Evidence Use", "Demonstrates analytical curiosity."],
        [509, "Decision-Making and Judgement", "Demonstrates prudent platform judgment."],
        [510, "Innovation and Transformation", "Demonstrates openness to experimentation."],
      ],
      behaviour: [
        [511, "Selects channels based on audience behaviour and campaign", "Selects channels based on audience behaviour and campaign objectives."],
        [512, "Integrates paid, owned, earned and partner channels appropriately", "Integrates paid, owned, earned and partner channels appropriately."],
        [513, "Optimizes digital journeys using performance and user-experience data", "Optimizes digital journeys using performance and user-experience data."],
        [514, "Change Management", "Adapts channel plans as algorithms, costs and audience habits change."],
        [515, "Ensuring tracking and attribution methods are sufficiently reliable", "Ensures tracking and attribution methods are sufficiently reliable."],
        [516, "Evaluation of new channels through controlled tests before", "Evaluates new channels through controlled tests before substantial investment."],
      ],
    },
    {
      name: 'Marketing Analytics, Attribution & ROI',
      characteristic: [
        [517, "Analytical Thinking and Evidence Use", "Demonstrates quantitative confidence."],
        [518, "Skepticism about simplistic attribution", "Demonstrates skepticism about simplistic attribution."],
        [519, "Focus on commercial impact", "Demonstrates focus on commercial impact."],
        [520, "Understanding of measurement limits", "Demonstrates understanding of measurement limits."],
        [521, "Decision-Making and Judgement", "Demonstrates data-informed judgment."],
        [522, "Accountability for marketing investment", "Demonstrates accountability for marketing investment."],
      ],
      behaviour: [
        [523, "Definition of KPIs linked to awareness, engagement, conversion", "Defines KPIs linked to awareness, engagement, conversion and business value."],
        [524, "Application of suitable attribution and incrementality methods where", "Uses suitable attribution and incrementality methods where feasible."],
        [525, "Connects marketing activity with sales, retention and revenue", "Connects marketing activity with sales, retention and revenue outcomes."],
        [526, "Reports performance transparently, including uncertainty and limitations", "Reports performance transparently, including uncertainty and limitations."],
        [527, "Analytical Thinking and Evidence Use", "Reallocates investment based on credible evidence of effectiveness."],
        [528, "Brand Management and Reputation", "Balances short-term ROI with longer-term brand and customer value."],
      ],
    },
    {
      name: 'Marketing Budget & Resource Allocation',
      characteristic: [
        [529, "Capital Allocation and Investment Appraisal", "Demonstrates investment discipline."],
        [530, "Planning and Prioritisation", "Demonstrates prioritization ability."],
        [531, "Financial Reporting and Analysis", "Demonstrates financial literacy."],
        [532, "Balanced portfolio thinking", "Demonstrates balanced portfolio thinking."],
        [533, "Accountability for spend", "Demonstrates accountability for spend."],
        [534, "Pragmatism about resource constraints", "Demonstrates pragmatism about resource constraints."],
      ],
      behaviour: [
        [535, "Financial Planning and Forecasting", "Allocates marketing budgets according to strategic priorities and evidence."],
        [536, "Planning and Prioritisation", "Builds plans with explicit assumptions, targets and contingencies."],
        [537, "Financial Planning and Forecasting", "Monitors spending and forecast variances throughout the period."],
        [538, "Comparative Analysis of channel and campaign effectiveness using", "Compares channel and campaign effectiveness using consistent methods."],
        [539, "Change Management", "Redirects resources when expected returns or priorities change."],
        [540, "Ensuring agency and production costs are controlled without", "Ensures agency and production costs are controlled without undermining quality."],
      ],
    },
    {
      name: 'Marketing Organization, Talent & Agency Leadership',
      characteristic: [
        [541, "Analytical Thinking and Evidence Use", "Demonstrates creative and analytical balance."],
        [542, "Talent Development and Capability Building", "Demonstrates commitment to talent growth."],
        [543, "Collaboration and Relationship Building", "Demonstrates collaborative leadership."],
        [544, "Appreciation of diverse perspectives", "Demonstrates appreciation of diverse perspectives."],
        [545, "Adaptability", "Demonstrates adaptability."],
        [546, "Standards of professional conduct", "Demonstrates high standards of professional conduct."],
      ],
      behaviour: [
        [547, "Setting of roles and performance expectations for marketing", "Sets clear roles and performance expectations for marketing teams."],
        [548, "Data Governance and Analytics", "Develops capabilities in brand, digital, analytics, content and customer insight."],
        [549, "Manages agencies against clear briefs, outcomes and accountability", "Manages agencies against clear briefs, outcomes and accountability."],
        [550, "Analytical Thinking and Evidence Use", "Integrates creative, commercial and analytical perspectives in decisions."],
        [551, "Building productive relationships with sales, product and corporate", "Builds productive relationships with sales, product and corporate functions."],
        [552, "Review of team structure and skills against evolving", "Reviews team structure and skills against evolving marketing needs."],
      ],
    },
    {
      name: 'Marketing Strategy & Brand Positioning',
      characteristic: [
        [553, "Decision-Making and Judgement", "Demonstrates strategic marketing judgment."],
        [554, "Brand Management and Reputation", "Demonstrates understanding of brand differentiation."],
        [555, "Customer and User Focus", "Demonstrates customer and market curiosity."],
        [556, "Brand Management and Reputation", "Demonstrates long-term brand perspective."],
        [557, "Commercial orientation", "Demonstrates commercial orientation."],
        [558, "Clarity of positioning", "Demonstrates clarity of positioning."],
      ],
      behaviour: [
        [559, "Strategic Thinking and Alignment", "Translates corporate strategy into a differentiated marketing strategy."],
        [560, "Commercial and Business Acumen", "Defines target segments, value propositions and positioning clearly."],
        [561, "Commercial and Business Acumen", "Aligns brand choices with customer needs and competitive realities."],
        [562, "Planning and Prioritisation", "Prioritizes marketing initiatives against strategic objectives."],
        [563, "Customer and User Focus", "Reviews positioning as market conditions and customer expectations evolve."],
        [564, "Brand Management and Reputation", "Measures brand strength and contribution to business outcomes."],
      ],
    },
    {
      name: 'Marketing Technology, Data Privacy & Responsible AI',
      characteristic: [
        [565, "Technology curiosity", "Demonstrates technology curiosity."],
        [566, "Customer and User Focus", "Demonstrates respect for customer privacy."],
        [567, "Integrity and Ethical Leadership", "Demonstrates awareness of data ethics."],
        [568, "Decision-Making and Judgement", "Demonstrates prudent automation judgment."],
        [569, "Security consciousness", "Demonstrates security consciousness."],
        [570, "Skepticism toward opaque tools", "Demonstrates skepticism toward opaque tools."],
      ],
      behaviour: [
        [571, "Customer and User Focus", "Governs marketing technology, customer data and platform access appropriately."],
        [572, "Ensuring consent, preference and privacy requirements are respected", "Ensures consent, preference and privacy requirements are respected."],
        [573, "Data Governance and Analytics", "Assesses AI-generated content and targeting for accuracy, bias and brand risk."],
        [574, "Data Governance and Analytics", "Integrates marketing tools while maintaining data quality and control."],
        [575, "Review of vendor terms, tracking practices and platform", "Reviews vendor terms, tracking practices and platform dependencies."],
        [576, "Customer Experience and Retention", "Measures whether technology improves marketing effectiveness and customer experience."],
      ],
    },
    {
      name: 'Pricing, Proposition & Commercial Growth',
      characteristic: [
        [577, "Decision-Making and Judgement", "Demonstrates commercial judgment."],
        [578, "Sensitivity to willingness to pay", "Demonstrates sensitivity to willingness to pay."],
        [579, "Value-based thinking", "Demonstrates value-based thinking."],
        [580, "Awareness of margin implications", "Demonstrates awareness of margin implications."],
        [581, "Comfort with market trade-offs", "Demonstrates comfort with market trade-offs."],
        [582, "Diversity, Equity and Inclusion", "Demonstrates customer fairness orientation."],
      ],
      behaviour: [
        [583, "Analytical Thinking and Evidence Use", "Uses customer and competitive evidence to inform pricing and packaging."],
        [584, "Change Management", "Tests proposition changes and evaluates effects on conversion and margin."],
        [585, "Collaborative Working on with finance and sales to", "Works with finance and sales to assess discounting and promotional economics."],
        [586, "Identification of opportunities to improve cross-sell, upsell and", "Identifies opportunities to improve cross-sell, upsell and market penetration."],
        [587, "Ensuring claims and offers are , fair and", "Ensures claims and offers are clear, fair and compliant."],
        [588, "Customer and User Focus", "Monitors the impact of pricing and proposition decisions on customer value."],
      ],
    },
    {
      name: 'Product Marketing & Go-to-Market',
      characteristic: [
        [589, "Customer and User Focus", "Demonstrates understanding of customer value."],
        [590, "Commercial awareness", "Demonstrates commercial awareness."],
        [591, "Ability to simplify complexity", "Demonstrates ability to simplify complexity."],
        [592, "Cross-Functional Coordination", "Demonstrates cross-functional orientation."],
        [593, "Decision-Making and Judgement", "Demonstrates market-launch judgment."],
        [594, "Attention to competitive differentiation", "Demonstrates attention to competitive differentiation."],
      ],
      behaviour: [
        [595, "Commercial and Business Acumen", "Develops clear positioning, messaging and value propositions for offerings."],
        [596, "Planning and Prioritisation", "Coordinates go-to-market plans with product, sales and service teams."],
        [597, "Equips sales teams with relevant insights, materials and", "Equips sales teams with relevant insights, materials and competitive guidance."],
        [598, "Analytical Thinking and Evidence Use", "Validates pricing and packaging propositions with customer and market evidence."],
        [599, "Change Management", "Monitors launch performance and responds to adoption barriers."],
        [600, "Customer and User Focus", "Uses customer feedback to improve future product and market decisions."],
      ],
    },
  ],
}
